import React, { useState, useRef, useEffect } from 'react';
import { logger } from '@/lib/logger';
import { useFloorPlanScale } from '@/hooks/useFloorPlanScale';
import { calculateDistance } from '@/utils/scaleCalculations';
import { Point } from '@/types/common';
import { useAnnotations } from '@/hooks/useAnnotations';
import { useAppStore } from '@/stores/appStore';

interface MeasurementOverlayProps {
  floorPlanId: string;
  isActive: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  onMeasurementComplete?: (pixelDistance: number) => void;
  setIsMeasuring?: (value: boolean) => void;
}

interface Measurement {
  id: string;
  start: Point; // Floor plan coordinates
  end: Point; // Floor plan coordinates
  startViewport: Point; // Viewport coordinates for display
  endViewport: Point; // Viewport coordinates for display
  distance: string;
}

export function MeasurementOverlay({
  floorPlanId,
  isActive,
  containerRef,
  onMeasurementComplete,
  setIsMeasuring,
}: MeasurementOverlayProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [endPoint, setEndPoint] = useState<Point | null>(null);
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [containerSize, setContainerSize] = useState({ width: 1920, height: 1080 });
  const svgRef = useRef<SVGSVGElement>(null);
  const isCalibrationMode = useRef(false);

  const { formatMeasurement, hasScale, refetch: refetchScale } = useFloorPlanScale(floorPlanId);
  const { createAnnotation } = useAnnotations(floorPlanId);
  const currentStyle = useAppStore(state => state.annotations.currentStyle);

  // Update container size
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        setContainerSize({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [containerRef]);

  // Convert floor plan coordinates to viewport coordinates for display
  const toViewportCoords = (point: Point): Point => {
    if (!containerRef.current) return point;

    const transformedElement = containerRef.current.querySelector(
      '[style*="transform"][style*="scale"]',
    ) as HTMLElement;
    if (!transformedElement) return point;

    const transform = transformedElement.style.transform || '';
    const scaleMatch = transform.match(/scale\(([\d.]+)\)/);
    const translateMatch = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);

    const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
    const translateX = translateMatch ? parseFloat(translateMatch[1]) : 0;
    const translateY = translateMatch ? parseFloat(translateMatch[2]) : 0;

    return {
      x: point.x * scale + translateX,
      y: point.y * scale + translateY,
    };
  };

  // Get relative coordinates from mouse event
  const getRelativeCoords = (e: MouseEvent): Point | null => {
    if (!containerRef.current) return null;

    const rect = containerRef.current.getBoundingClientRect();

    // Get viewport-relative coordinates
    const viewportX = e.clientX - rect.left;
    const viewportY = e.clientY - rect.top;

    // Find the transformed element containing the floor plan
    const transformedElement = containerRef.current.querySelector(
      '[style*="transform"][style*="scale"]',
    ) as HTMLElement;

    if (!transformedElement) {
      logger.warn('No transformed element found');
      return null;
    }

    // Parse the transform to get scale and translation
    const transform = transformedElement.style.transform || '';
    const scaleMatch = transform.match(/scale\(([\d.]+)\)/);
    const translateMatch = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);

    const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
    const translateX = translateMatch ? parseFloat(translateMatch[1]) : 0;
    const translateY = translateMatch ? parseFloat(translateMatch[2]) : 0;

    // Convert viewport coordinates to floor plan coordinates
    const x = (viewportX - translateX) / scale;
    const y = (viewportY - translateY) / scale;

    logger.log('Viewport coords:', { viewportX, viewportY });
    logger.log('Transform:', { scale, translateX, translateY });
    logger.log('Floor plan coords:', { x, y });

    return { x, y };
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isActive) return;
    e.stopPropagation();
    e.preventDefault();

    const coords = getRelativeCoords(e.nativeEvent as MouseEvent);
    logger.log('Mouse down coords:', coords);
    if (coords) {
      setIsDrawing(true);
      setStartPoint(coords);
      setEndPoint(coords);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDrawing || !startPoint) return;
    e.stopPropagation();

    const coords = getRelativeCoords(e.nativeEvent as MouseEvent);
    if (coords) {
      // Apply shift key constraint for horizontal/vertical lines
      if (e.shiftKey) {
        const dx = Math.abs(coords.x - startPoint.x);
        const dy = Math.abs(coords.y - startPoint.y);

        if (dx > dy) {
          // Horizontal constraint
          setEndPoint({ x: coords.x, y: startPoint.y });
        } else {
          // Vertical constraint
          setEndPoint({ x: startPoint.x, y: coords.y });
        }
      } else {
        setEndPoint(coords);
      }
    }
  };

  const handleMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!isDrawing || !startPoint || !endPoint) return;
    e.stopPropagation();

    // Calculate and save measurement
    const distance = calculateDistance(startPoint, endPoint);

    // Prevent creating zero-length dimensions (minimum 5 pixels)
    if (distance < 5) {
      // Reset drawing state without creating dimension
      setIsDrawing(false);
      setStartPoint(null);
      setEndPoint(null);
      return;
    }

    const formattedDistance = formatMeasurement(distance);

    // Call the callback with pixel distance if provided
    if (onMeasurementComplete) {
      isCalibrationMode.current = true;
      onMeasurementComplete(distance);
    }

    // Only save measurement if not in calibration mode and scale is set
    if (!isCalibrationMode.current && hasScale) {
      // Create measurement annotation
      createAnnotation({
        floor_plan_id: floorPlanId,
        type: 'measurement',
        data: {
          start: startPoint,
          end: endPoint,
        },
        style: {
          strokeColor: currentStyle.strokeColor || '#3b82f6',
          strokeWidth: currentStyle.strokeWidth || 2,
          opacity: currentStyle.opacity || 1,
          fontSize: 14,
        },
      });

      // Toggle off the measurement tool after creating a dimension
      if (setIsMeasuring) {
        setIsMeasuring(false);
      }
    }

    // Reset drawing state
    setIsDrawing(false);
    setStartPoint(null);
    setEndPoint(null);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl/Cmd+C to clear all measurements
      if (e.key === 'c' && (e.metaKey || e.ctrlKey) && isActive) {
        setMeasurements([]);
      }
      // ESC to cancel current measurement - always reset state when tool is active
      // (resetting null state is harmless, avoids stale closure issues)
      if (e.key === 'Escape' && isActive) {
        e.preventDefault();
        e.stopPropagation();
        setIsDrawing(false);
        setStartPoint(null);
        setEndPoint(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);

  // Reset calibration mode when isActive changes and refetch scale data
  useEffect(() => {
    if (!isActive) {
      isCalibrationMode.current = false;
    } else {
      // When measurement mode becomes active, refetch scale data to ensure it's current
      // This is important when scale has just been set
      refetchScale();
    }
  }, [isActive, refetchScale]);

  if (!isActive && measurements.length === 0) return null;

  return (
    <svg
      ref={svgRef}
      className={`absolute inset-0 w-full h-full pointer-events-none`}
      style={{ zIndex: isActive ? 40 : 30 }}
      viewBox={`0 0 ${containerSize.width} ${containerSize.height}`}
      preserveAspectRatio="none"
    >
      {/* Background to capture mouse events */}
      {isActive && (
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.05)"
          style={{ cursor: 'crosshair', pointerEvents: 'auto' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        />
      )}

      {/* Render active measurement */}
      {isDrawing &&
        startPoint &&
        endPoint &&
        (() => {
          const startViewport = toViewportCoords(startPoint);
          const endViewport = toViewportCoords(endPoint);
          return (
            <g>
              <line
                x1={startViewport.x}
                y1={startViewport.y}
                x2={endViewport.x}
                y2={endViewport.y}
                stroke="#10b981"
                strokeWidth="2"
                strokeDasharray="5,5"
                markerEnd="url(#arrowhead-active)"
                markerStart="url(#arrowhead-active)"
              />
              <text
                x={(startViewport.x + endViewport.x) / 2}
                y={(startViewport.y + endViewport.y) / 2 - 10}
                fill="#10b981"
                fontSize="14"
                fontWeight="bold"
                textAnchor="middle"
                style={{
                  background: 'white',
                  paintOrder: 'stroke',
                  stroke: 'white',
                  strokeWidth: 3,
                  strokeLinejoin: 'round',
                }}
              >
                {formatMeasurement(calculateDistance(startPoint, endPoint))}
              </text>
            </g>
          );
        })()}

      {/* Arrow markers */}
      <defs>
        <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="0" refY="3" orient="auto">
          <polygon points="0 0, 6 3, 0 6" fill="#3b82f6" />
        </marker>
        <marker
          id="arrowhead-active"
          markerWidth="10"
          markerHeight="10"
          refX="0"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 6 3, 0 6" fill="#10b981" />
        </marker>
      </defs>

      {/* Instructions */}
      {isActive && (
        <text x="10" y="30" fill="#6b7280" fontSize="12" className="select-none">
          Click and drag to measure. Press Ctrl+C to clear measurements.
        </text>
      )}
    </svg>
  );
}
